import axios, { AxiosError, type AxiosRequestConfig } from 'axios'

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://xedien-api.devstudio.tech/api'

export interface ApiEnvelope<T> {
  success: boolean
  data: T
  message?: string | string[]
  errors?: unknown
  timestamp: string
}

export interface ApiError {
  status: number
  message: string
  errors?: unknown
}

const ACCESS_KEY  = 'xedien.auth.accessToken'
const REFRESH_KEY = 'xedien.auth.refreshToken'

export const tokenStore = {
  getAccess()  { try { return localStorage.getItem(ACCESS_KEY)  } catch { return null } },
  getRefresh() { try { return localStorage.getItem(REFRESH_KEY) } catch { return null } },
  set(a: string, r: string) {
    try { localStorage.setItem(ACCESS_KEY, a); localStorage.setItem(REFRESH_KEY, r) } catch { /* ignore */ }
  },
  clear() {
    try { localStorage.removeItem(ACCESS_KEY); localStorage.removeItem(REFRESH_KEY) } catch { /* ignore */ }
  },
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
})

// Attach Bearer
apiClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccess()
  if (token) {
    config.headers = config.headers ?? {}
    ;(config.headers as any).Authorization = `Bearer ${token}`
  }
  return config
})

// Refresh token on 401 — queue concurrent requests
let refreshing = false
let waitQueue: Array<(ok: boolean) => void> = []

async function tryRefresh(): Promise<boolean> {
  const rt = tokenStore.getRefresh()
  if (!rt) return false
  try {
    const res = await axios.post<ApiEnvelope<{ accessToken: string; refreshToken: string }>>(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken: rt },
      { headers: { 'Content-Type': 'application/json' } },
    )
    const d = res.data?.data
    if (d?.accessToken && d?.refreshToken) {
      tokenStore.set(d.accessToken, d.refreshToken)
      return true
    }
    return false
  } catch { return false }
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined
    const status = error.response?.status ?? 0

    if (status === 401 && original && !original._retry) {
      original._retry = true
      if (refreshing) {
        const ok = await new Promise<boolean>((resolve) => waitQueue.push(resolve))
        if (!ok) throw error
        return apiClient(original)
      }
      refreshing = true
      const ok = await tryRefresh()
      refreshing = false
      waitQueue.splice(0).forEach((cb) => cb(ok))
      if (!ok) {
        tokenStore.clear()
        throw error
      }
      return apiClient(original)
    }

    throw error
  },
)

// Unwrap helper: throws ApiError on HTTP error, returns data otherwise
export async function callApi<T>(fn: () => Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  try {
    const res = await fn()
    if (!res.data?.success) {
      throw { status: 0, message: Array.isArray(res.data?.message) ? res.data.message.join(', ') : (res.data?.message ?? 'Lỗi không xác định') } satisfies ApiError
    }
    return res.data.data
  } catch (e) {
    const err = e as AxiosError<ApiEnvelope<unknown>>
    const status = err.response?.status ?? 0
    const payload = err.response?.data
    const msg = Array.isArray(payload?.message) ? payload!.message.join(', ') : (payload?.message as string | undefined)
    throw {
      status,
      message: msg ?? err.message ?? 'Lỗi mạng',
      errors: (payload as any)?.errors,
    } satisfies ApiError
  }
}

// Convenience methods
export const api = {
  get:    <T>(url: string, config?: AxiosRequestConfig) => callApi<T>(() => apiClient.get(url, config)),
  post:   <T>(url: string, body?: unknown, config?: AxiosRequestConfig) => callApi<T>(() => apiClient.post(url, body, config)),
  patch:  <T>(url: string, body?: unknown, config?: AxiosRequestConfig) => callApi<T>(() => apiClient.patch(url, body, config)),
  put:    <T>(url: string, body?: unknown, config?: AxiosRequestConfig) => callApi<T>(() => apiClient.put(url, body, config)),
  delete: <T>(url: string, config?: AxiosRequestConfig) => callApi<T>(() => apiClient.delete(url, config)),
}
